<script setup lang="ts">
/**
 * The ways to reach this board from somewhere other than this tab: your own
 * Claude Code over MCP, the Android app, and notifications on this device.
 * What the whole server is set to (the priming prompt, the task prompt) is
 * the admins' Global settings page, reached from the stats view.
 */
import { onBeforeUnmount, onMounted, ref } from "vue";
import { toast } from "../lib/toast.js";
import { copyText } from "../lib/clipboard.js";
import { disableNotifications, enableNotifications, notificationsOn, notifySupport } from "../lib/notifications.js";

const emit = defineEmits<{ close: [] }>();

/**
 * Built from the page's origin, so it names whichever deployment they are
 * looking at. Shown in full as well as copied, because a silent clipboard
 * write is invisible and the command is worth reading before pasting.
 */
const mcpCommand = `claude mcp add --transport http todos ${location.origin}/mcp`;

async function copyMcp(): Promise<void> {
  await copyText(mcpCommand);
  toast("Copied the Claude Code command");
}

// ── notifications ─────────────────────────────────────────────────────────
// Per device, not per person: the switch says whether THIS browser or phone
// is told, and switching it on the laptop leaves the phone as it was.
const support = notifySupport();
/** Inside the app, the APK is how it got here: offer its own updater instead. */
const checkForUpdate = window.todosAndroid?.checkForUpdate?.bind(window.todosAndroid);
/** Null while it is being looked up. */
const notifyOn = ref<boolean | null>(null);
const notifyBusy = ref(false);
const notifyError = ref("");

async function toggleNotify(ev: Event): Promise<void> {
  const want = (ev.target as HTMLInputElement).checked;
  notifyBusy.value = true;
  notifyError.value = "";
  try {
    if (want) await enableNotifications();
    else await disableNotifications();
    notifyOn.value = want;
  } catch (err) {
    notifyError.value = err instanceof Error ? err.message : String(err);
    notifyOn.value = await notificationsOn().catch(() => false);
  } finally {
    notifyBusy.value = false;
    // v-model is not used, so a refused change has to be put back by hand.
    (ev.target as HTMLInputElement).checked = notifyOn.value === true;
  }
}

function onKeydown(ev: KeyboardEvent): void {
  if (ev.key === "Escape") {
    ev.stopPropagation();
    emit("close");
  }
}

onMounted(() => {
  if (support !== "none") {
    notificationsOn().then(
      (on) => (notifyOn.value = on),
      () => (notifyOn.value = false),
    );
  }
  document.body.style.overflow = "hidden";
  window.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => {
  document.body.style.overflow = "";
  window.removeEventListener("keydown", onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div class="modal-backdrop" @pointerdown.self="emit('close')">
      <div class="modal integrations-modal" role="dialog" aria-modal="true" aria-labelledby="integrations-title">
        <header class="modal-head">
          <span id="integrations-title">Integrations</span>
          <span style="flex: 1"></span>
          <button class="btn ghost" type="button" title="Close" aria-label="Close" @click="emit('close')">✕</button>
        </header>

        <div class="modal-body">
          <section class="integration">
            <div class="integration-head">Claude Code</div>
            <p class="modal-lede">
              Run this once in a terminal. Claude Code opens the browser to sign in with your account here,
              then can read and change tasks on this board.
            </p>
            <div class="integration-row">
              <code class="integration-code">{{ mcpCommand }}</code>
              <button class="btn" type="button" @click="copyMcp">Copy</button>
            </div>
          </section>

          <section class="integration">
            <div class="integration-head">Notifications</div>
            <p class="modal-lede">
              When you are assigned a task, someone comments on one of yours, a team message is posted, or a
              Claude run you asked for finishes.
              <template v-if="support === 'android'">
                The phone checks every 15 minutes or so, so one can take that long to arrive.
              </template>
              <template v-else>Not shown while you are looking at the board.</template>
            </p>
            <div class="integration-row">
              <label v-if="support !== 'none'" class="integration-switch">
                <input
                  type="checkbox"
                  :checked="notifyOn === true"
                  :disabled="notifyOn === null || notifyBusy"
                  @change="toggleNotify"
                />
                Notify me on this {{ support === "android" ? "phone" : "browser" }}
              </label>
              <span v-else class="integration-muted">This browser can't show notifications from a web page.</span>
            </div>
            <p v-if="notifyError" class="integration-error">{{ notifyError }}</p>
          </section>

          <section class="integration">
            <div class="integration-head">Android app</div>
            <p v-if="checkForUpdate" class="modal-lede">
              You are in it. It looks for a new version each time it opens; this asks now.
            </p>
            <p v-else class="modal-lede">
              Open this page on the phone, download, and open the file to install. Android asks once to allow
              installs from your browser. The app keeps itself up to date after that.
            </p>
            <div class="integration-row">
              <button v-if="checkForUpdate" type="button" class="btn" @click="checkForUpdate()">Check for updates</button>
              <a v-else class="btn" href="/api/app.apk" download>Download APK</a>
            </div>
          </section>
        </div>
      </div>
    </div>
  </Teleport>
</template>
